import React, { useEffect, useRef, useCallback } from 'react'

// ─── Constants ────────────────────────────────────────────────────────────────
const RINGS = [
  { radiusOffset: 0, baseAlpha: 0.85, color: [0, 104, 255] },   // #0068ff – core
  { radiusOffset: 10, baseAlpha: 0.5,  color: [0, 168, 255] },  // #00a8ff
  { radiusOffset: 22, baseAlpha: 0.28, color: [0, 212, 255] },  // #00d4ff
  { radiusOffset: 36, baseAlpha: 0.13, color: [99, 160, 255] }  // #63a0ff – outer halo
]

const AI_COLORS = [
  [120, 80, 255],  // violet
  [80, 200, 255],  // cyan
  [60, 220, 180],  // teal
  [160, 100, 255]  // purple halo
]

const BASE_RADIUS = 34   // px – core ring radius on canvas
const CANVAS_SIZE = 200  // px

// ─── MicrophoneLayout ─────────────────────────────────────────────────────────
const MicrophoneLayout = ({
  connectedToLiveKit = 'idle',
  isSpeaking = null
}) => {
  const canvasRef = useRef(null)
  const rafRef = useRef(null)
  const analyserRef = useRef(null)
  const streamRef = useRef(null)
  const audioCtxRef = useRef(null)
  const smoothedRef = useRef(0)           // smoothed amplitude 0-1
  const simPhaseRef = useRef(0)           // phase for AI simulation
  const connectedRef = useRef(connectedToLiveKit)
  const isSpeakingRef = useRef(isSpeaking)

  // Keep refs in sync so RAF closure always sees fresh values
  useEffect(() => { connectedRef.current = connectedToLiveKit }, [connectedToLiveKit])
  useEffect(() => { isSpeakingRef.current = isSpeaking }, [isSpeaking])

  // ── Start real mic analyser ────────────────────────────────────────────────
  const startAnalyser = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const ctx = new (window.AudioContext || window.webkitAudioContext)()
      const source = ctx.createMediaStreamSource(stream)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.75
      source.connect(analyser)
      streamRef.current = stream
      audioCtxRef.current = ctx
      analyserRef.current = analyser
    } catch {
      // Permission denied or no mic — fallback to simulated amplitude
      analyserRef.current = null
    }
  }, [])

  // ── Stop mic analyser ─────────────────────────────────────────────────────
  const stopAnalyser = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {})
      audioCtxRef.current = null
    }
    analyserRef.current = null
  }, [])

  // ── Get amplitude 0-1 from analyser ──────────────────────────────────────
  const getAmplitude = useCallback(() => {
    const analyser = analyserRef.current
    if (!analyser) return 0
    const data = new Uint8Array(analyser.frequencyBinCount)
    analyser.getByteFrequencyData(data)
    const avg = data.reduce((s, v) => s + v, 0) / data.length
    return avg / 255
  }, [])

  // ── Main draw loop ────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    rafRef.current = requestAnimationFrame(draw)

    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const W = canvas.width
    const H = canvas.height
    const cx = W / 2
    const cy = H / 2

    ctx.clearRect(0, 0, W, H)

    const state = connectedRef.current
    const speaking = isSpeakingRef.current

    // ── Determine target amplitude ──────────────────────────────────────────
    let targetAmp = 0

    if (state === 'connecting' || state === 'reconnecting') {
      // Steady pulse for connecting state
      simPhaseRef.current += 0.04
      targetAmp = 0.35 + 0.2 * Math.sin(simPhaseRef.current)
    } else if (state === 'connected') {
      if (speaking === 'user') {
        // Read real mic
        targetAmp = getAmplitude()
        // Boost quiet signals so rings are visible even at low volume
        targetAmp = Math.pow(targetAmp * 2.5, 0.7)
        targetAmp = Math.min(targetAmp, 1)
      } else if (speaking === 'ai') {
        // Simulate AI speech — layered sinusoids at speech-like frequencies
        simPhaseRef.current += 0.055
        const base  = 0.45 + 0.35 * Math.sin(simPhaseRef.current)
        const fast  = 0.12 * Math.sin(simPhaseRef.current * 3.7)
        const micro = 0.06 * Math.sin(simPhaseRef.current * 8.1)
        targetAmp = Math.min(Math.max(base + fast + micro, 0), 1)
      } else {
        // Connected, silence — gentle breathing
        simPhaseRef.current += 0.018
        targetAmp = 0.08 + 0.06 * Math.sin(simPhaseRef.current)
      }
    } else if (state === 'idle') {
      // Very subtle breathing animation so UI isn't static
      simPhaseRef.current += 0.012
      targetAmp = 0.04 + 0.03 * Math.sin(simPhaseRef.current)
    }

    // Smooth the value
    const smoothFactor = speaking ? 0.22 : 0.08
    smoothedRef.current += (targetAmp - smoothedRef.current) * smoothFactor
    const amp = smoothedRef.current

    // ── Choose ring colours ─────────────────────────────────────────────────
    const palette = (speaking === 'ai') ? AI_COLORS : RINGS.map(r => r.color)

    // ── Draw rings outward → inward ─────────────────────────────────────────
    for (let i = RINGS.length - 1; i >= 0; i--) {
      const ring = RINGS[i]
      const spread = amp * (42 + i * 14)   // how much amp expands this ring
      const r = BASE_RADIUS + ring.radiusOffset + spread
      const alpha = ring.baseAlpha * (0.4 + amp * 0.6)
      const [rC, gC, bC] = palette[i]

      // Radial glow gradient
      const grad = ctx.createRadialGradient(cx, cy, r * 0.6, cx, cy, r)
      grad.addColorStop(0, `rgba(${rC},${gC},${bC},${alpha})`)
      grad.addColorStop(1, `rgba(${rC},${gC},${bC},0)`)

      ctx.beginPath()
      ctx.arc(cx, cy, r, 0, Math.PI * 2)
      ctx.fillStyle = grad
      ctx.fill()
    }

    // ── Core circle (solid) ─────────────────────────────────────────────────
    const coreScale = 1 + amp * 0.18
    const coreR = BASE_RADIUS * coreScale
    const [cR, cG, cB] = palette[0]

    const coreGrad = ctx.createRadialGradient(cx, cy - 4, 2, cx, cy, coreR)
    coreGrad.addColorStop(0,   `rgba(${cR + 60},${cG + 60},${cB + 60},0.95)`)
    coreGrad.addColorStop(0.5, `rgba(${cR},${cG},${cB},0.92)`)
    coreGrad.addColorStop(1,   `rgba(${Math.max(cR-40,0)},${Math.max(cG-30,0)},${Math.min(cB+20,255)},0.85)`)

    ctx.beginPath()
    ctx.arc(cx, cy, coreR, 0, Math.PI * 2)
    ctx.fillStyle = coreGrad
    ctx.fill()

    // ── Mic icon (Original SVG perfectly mapped to canvas) ────────────────
    ctx.save()
    
    // The original SVG has a 24x24 viewBox perfectly centered at 12, 12
    const iconScale = 1.15; // Adjusted size (visually smaller than original 30px CSS icon)
    
    // Shift the context so the 12,12 center of the SVG path lands exactly on cx,cy
    ctx.translate(cx - 12 * iconScale, cy - 12 * iconScale)
    ctx.scale(iconScale, iconScale)

    ctx.strokeStyle = 'rgba(255,255,255,0.95)'
    ctx.lineWidth = 2.2 / iconScale // Keeps line thickness crisp regardless of scale
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    // Combine original SVG paths into a single Path2D:
    // Body: M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z
    // Arc: M19 10v2a7 7 0 0 1-14 0v-2
    // Stem: M12 19v4  (equivalent to line x1=12 y1=19 x2=12 y2=23)
    // Base: M8 23h8   (equivalent to line x1=8 y1=23 x2=16 y2=23)
    const micPath = new Path2D(
      'M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z ' +
      'M19 10v2a7 7 0 0 1-14 0v-2 ' +
      'M12 19v4 ' +
      'M8 23h8'
    )
    
    ctx.stroke(micPath)
    ctx.restore()

    // ── Connecting spinner ─────────────────────────────────────────────────
    if (state === 'connecting' || state === 'reconnecting') {
      const spinR = BASE_RADIUS + 50
      const angle = Date.now() / 600
      const arcLen = Math.PI * 1.3

      ctx.save()
      ctx.translate(cx, cy)
      const spinGrad = ctx.createLinearGradient(
        Math.cos(angle) * spinR, Math.sin(angle) * spinR,
        Math.cos(angle + arcLen) * spinR, Math.sin(angle + arcLen) * spinR
      )
      spinGrad.addColorStop(0, 'rgba(0,104,255,0)')
      spinGrad.addColorStop(0.5, 'rgba(0,212,255,0.8)')
      spinGrad.addColorStop(1, 'rgba(0,104,255,0)')

      ctx.beginPath()
      ctx.arc(0, 0, spinR, angle, angle + arcLen)
      ctx.strokeStyle = spinGrad
      ctx.lineWidth = 3
      ctx.stroke()
      ctx.restore()
    }
  }, [getAmplitude])

  // ── Lifecycle: start/stop mic, always run draw loop ───────────────────────
  useEffect(() => {
    // Start RAF loop
    rafRef.current = requestAnimationFrame(draw)

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [draw])

  useEffect(() => {
    if (connectedToLiveKit === 'connected') {
      startAnalyser()
    } else {
      stopAnalyser()
    }
    return () => {}
  }, [connectedToLiveKit, startAnalyser, stopAnalyser])

  // ── State label ───────────────────────────────────────────────────────────
  const stateLabel = (() => {
    if (connectedToLiveKit === 'connecting') return 'Connecting…'
    if (connectedToLiveKit === 'reconnecting') return 'Reconnecting…'
    if (connectedToLiveKit === 'connected') {
      if (isSpeaking === 'user') return 'Listening…'
      if (isSpeaking === 'ai')   return 'Speaking…'
      return 'Connected'
    }
    return null
  })()

  return (
    <div className="siri-mic-wrapper">
      <style>{`
        .siri-mic-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          user-select: none;
        }

        .siri-canvas {
          display: block;
          border-radius: 50%;
          cursor: pointer;
          filter: drop-shadow(0 0 18px rgba(0,104,255,0.35));
          transition: filter 0.3s ease;
        }

        .siri-canvas:hover {
          filter: drop-shadow(0 0 28px rgba(0,168,255,0.55));
        }

        .siri-state-label {
          position: absolute;
          bottom: -30px;
          left: 50%;
          transform: translateX(-50%);
          white-space: nowrap;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(0, 140, 255, 0.75);
          font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', sans-serif;
          transition: opacity 0.3s ease;
        }
      `}</style>

      <canvas
        ref={canvasRef}
        width={CANVAS_SIZE}
        height={CANVAS_SIZE}
        className="siri-canvas"
      />

      {stateLabel && (
        <span className="siri-state-label">{stateLabel}</span>
      )}
    </div>
  )
}

export default MicrophoneLayout
